import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, storage } from '../utils/FirebaseConfig'; // Adjust the path according to your project structure
import { doc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { toast } from 'react-toastify';
import ZonJakim from '../components/ZonJakim';
import { FaTrash } from 'react-icons/fa';
import { getStorageRefFromUrl } from '../utils/storageUtils';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { createImageVariants } from "../utils/compressImage";
import { savePoster, saveSettings } from "../services/mosqueContentRepository";



import 'react-toastify/dist/ReactToastify.css';
import '../styles/Etv.css';

const defaultPrayerSettings = {
    Subuh: { masaIqamah: 15, skrinGelap: 15 },
    Zohor: { masaIqamah: 10, skrinGelap: 15 },
    Jumaat: { masaIqamah: 0, skrinGelap: 30 },
    Asar: { masaIqamah: 10, skrinGelap: 15 },
    Maghrib: { masaIqamah: 10, skrinGelap: 15 },
    Isyak: { masaIqamah: 10, skrinGelap: 30 }
};

const Etv = ({ isMobile }) => {
    const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
    const mosqueId = userProfile.mosqueId;

    const [images, setImages] = useState([]);
    const [videoUrl, setVideoUrl] = useState(null); // For storing video URL
    const [values, setValues] = useState(defaultPrayerSettings);
    const [isUploading, setIsUploading] = useState(false); // Add a flag to track the upload state
    const [isPosterMutating, setIsPosterMutating] = useState(false);
    const posterMutationRef = useRef(false);
    const [etvColor, setEtvColor] = useState('#7A1777'); // default color
    const [etvSound, setEtvSound] = useState('beduk'); // default "beduk"
    const MAX_POSTERS = 24;
    const [showMeccaButton, setShowMeccaButton] = useState(false);
    const [meccaLive, setMeccaLive] = useState(false);
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [autoSavedAt, setAutoSavedAt] = useState(null);
    const isHydratingRef = useRef(true);
    const [forceBlackout, setForceBlackout] = useState(false);



    const fetchSettings = useCallback(async () => {
        try {
            if (!mosqueId) {
                console.error("Mosque ID is not defined");
                return;
            }

            const docRef = doc(db, 'masajid', mosqueId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data().etv || {};
                setImages(data.etvImage || []);
                setVideoUrl(data.videoUrl || null);
                setValues(data.prayerSetting || defaultPrayerSettings);
                setEtvColor(data.etvColor || "#7A1777");
                setEtvSound(data.etvSound || "beduk");

                // ✅ NEW: mecca settings
                setShowMeccaButton(!!data.showMeccaButton);
                setMeccaLive(!!data.meccaLive);
                setForceBlackout(!!data.forceBlackout); // ✅ NEW

            }
            else {
                // If document does not exist, create it with default values
                await saveSettings(mosqueId, { prayerSetting: defaultPrayerSettings, etvImage: [], videoUrl: null });
                console.log("Created new document in Firestore with default values");
            }
        } catch (error) {
            console.error("Error fetching document: ", error);
            toast.error('Error fetching data from Firestore.');
        }
    }, [mosqueId]);



    useEffect(() => {
        fetchSettings().finally(() => {
            // ✅ elak auto-save trigger masa initial load
            isHydratingRef.current = false;
        });
    }, [fetchSettings]);

    const saveEtvPartial = useCallback(
        async (patch) => {
            if (!mosqueId) return;
            await saveSettings(mosqueId, patch);
        },
        [mosqueId]
    );


    useEffect(() => {
        if (!mosqueId) return;
        if (isHydratingRef.current) return;

        const t = setTimeout(async () => {
            try {
                setIsAutoSaving(true);

                await saveEtvPartial({
                    prayerSetting: values,
                    etvColor,
                    etvSound,
                    meccaLive,
                    forceBlackout,
                });

                setAutoSavedAt(Date.now());
            } catch (e) {
                console.error("Auto-save failed:", e);
                toast.error("Auto-save gagal. Sila cuba lagi.");
            } finally {
                setIsAutoSaving(false);
            }
        }, 600); // debounce 600ms

        return () => clearTimeout(t);
    }, [mosqueId, values, etvColor, etvSound, meccaLive, forceBlackout, saveEtvPartial]);



    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // ✅ hard limit untuk elak file terlalu gila besar
        const hardLimit = 15 * 1024 * 1024; // 15MB
        if (file.size > hardLimit) {
            toast.error("File terlalu besar. Maksimum 15MB.");
            event.target.value = "";
            return;
        }

        // ✅ limit poster
        if ((images?.length || 0) >= MAX_POSTERS) {
            toast.error(`Sudah sampai maksima had poster (${MAX_POSTERS}). Sila padam poster lain dahulu.`);
            event.target.value = "";
            return;
        }

        if (posterMutationRef.current) {
            event.target.value = "";
            return;
        }

        posterMutationRef.current = true;
        setIsPosterMutating(true);
        try {
            setIsUploading(true);
            const variants = await createImageVariants(file);
            const poster = await savePoster(mosqueId, variants);
            setImages((current) => current.includes(poster.mediumUrl)
                ? current
                : [...current, poster.mediumUrl]);
            toast.success("Poster berjaya dimuat naik dalam saiz optimum.");
        } catch (error) {
            console.error("Error uploading image:", error);
            toast.error(error?.message || "Poster gagal dimuat naik. Poster lama dikekalkan.");
        } finally {
            setIsUploading(false);
            posterMutationRef.current = false;
            setIsPosterMutating(false);
            event.target.value = "";
        }
    };


    const handleVideoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const maxSizeInBytes = 25 * 1024 * 1024; // 25MB max size
        if (file.size > maxSizeInBytes) {
            toast.error('File size exceeds the maximum limit of 25MB.');
            return;
        }

        if (isUploading) return; // Prevent multiple uploads
        setIsUploading(true);

        try {
            // 🗑 Ensure all old videos are deleted before uploading a new one
            if (videoUrl) {
                try {
                    const storageRefOldVideo = getStorageRefFromUrl(videoUrl);
                    if (storageRefOldVideo) {
                        await deleteObject(storageRefOldVideo);
                        console.log("✅ Old video deleted successfully:", videoUrl);
                    }
                } catch (error) {
                    if (error.code === 'storage/object-not-found') {
                        console.warn("⚠️ Old video not found in storage, skipping deletion.");
                    } else {
                        throw error;
                    }
                }
            }

            // 🚀 Upload New Video
            const timestamp = Date.now();
            const fileExtension = file.name.split('.').pop();
            const newFileName = `${timestamp}.${fileExtension}`;
            const storageRef = ref(storage, `${mosqueId}/videoUrl/${newFileName}`);

            await uploadBytes(storageRef, file);
            const newVideoUrl = await getDownloadURL(storageRef);

            // 🔥 Save URL to Firestore
            await saveVideoToFirestore(newVideoUrl);

            setVideoUrl(newVideoUrl);
            toast.success('Video uploaded successfully!');
        } catch (error) {
            console.error("❌ Error uploading video:", error);
            toast.error('Error uploading video.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleVideoDelete = async () => {
        if (!videoUrl) return;
        if (!window.confirm("Padam video ini dari sistem? Tindakan ini tidak boleh dikembalikan.")) return;

        try {
            // 1. Delete dari Storage
            const storageRefVideo = getStorageRefFromUrl(videoUrl);
            if (storageRefVideo) {
                await deleteObject(storageRefVideo);
                console.log("✅ Video deleted from Storage:", videoUrl);
            }

            // 2. Remove dari Firestore
            await saveSettings(mosqueId, { videoUrl: null });
            setVideoUrl(null);

            toast.success('Video berjaya dipadam!');
        } catch (error) {
            console.error("Error deleting video: ", error);
            toast.error('Error deleting video.');
        }
    };




    const handleImageDelete = async (index) => {
        const imageUrl = images[index];
        if (!imageUrl || !mosqueId || posterMutationRef.current) return;

        posterMutationRef.current = true;
        setIsPosterMutating(true);
        try {
            const storageRef = ref(storage, imageUrl);
            try {
                await deleteObject(storageRef);
            } catch (error) {
                if (error.code !== 'storage/object-not-found') throw error;
            }

            const updatedImages = images.filter((_, i) => i !== index);
            await saveSettings(mosqueId, { etvImage: updatedImages });
            setImages(updatedImages);
            toast.success('Poster berjaya dipadam!');
        } catch (error) {
            console.error("Error deleting image: ", error);
            toast.error('Gagal memadam poster. Sila cuba lagi.');
        } finally {
            posterMutationRef.current = false;
            setIsPosterMutating(false);
        }
    };

    const handleInputChange = (event, prayerSetting, type) => {
        const newValue = parseInt(event.target.value);
        setValues((prevValues) => ({
            ...prevValues,
            [prayerSetting]: { ...prevValues[prayerSetting], [type]: newValue }
        }));
    };

    const saveVideoToFirestore = async (videoUrl) => {
        try {
            await saveSettings(mosqueId, { videoUrl });
            console.log("✅ Firestore updated with new video:", videoUrl);
        } catch (error) {
            console.error("❌ Error updating Firestore:", error);
            toast.error('Error saving video URL to Firestore.');
        }
    };




    const handleDragEnd = async (result) => {
        if (!result.destination || posterMutationRef.current) return;

        posterMutationRef.current = true;
        setIsPosterMutating(true);
        const reorderedImages = [...images];
        const [movedItem] = reorderedImages.splice(result.source.index, 1);
        reorderedImages.splice(result.destination.index, 0, movedItem);

        try {
            await saveSettings(mosqueId, { etvImage: reorderedImages });
            setImages(reorderedImages);
        } catch (error) {
            console.error("Error updating image order in Firestore: ", error);
            toast.error('Gagal menyimpan susunan poster.');
        } finally {
            posterMutationRef.current = false;
            setIsPosterMutating(false);
        }
    };


    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <h3>Poster ETV</h3>
                <input
                    type="file"
                    accept="image/*"
                    id="fileInput"
                    style={{ display: "none" }}
                    onChange={handleImageUpload}
                    disabled={isPosterMutating}
                />
                <button
                    className='button'
                    onClick={() => document.getElementById("fileInput").click()}
                    disabled={isPosterMutating}
                >
                    Tambah
                </button>
            </div>


            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="posters" direction="horizontal">
                    {(provided) => (
                        <div className="image-grid" {...provided.droppableProps} ref={provided.innerRef}>
                            {images.map((image, index) => (
                                <Draggable
                                    key={image}
                                    draggableId={image}
                                    index={index}
                                    isDragDisabled={isPosterMutating}
                                >
                                    {(provided) => (
                                        <div
                                            className="image-item"
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                        >
                                            <img src={image} alt={`ETV ${index}`} />
                                            <button
                                                onClick={() => handleImageDelete(index)}
                                                disabled={isPosterMutating}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>


            {/* Video upload section */}
            <hr style={{ margin: "30px 0" }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <h3>Video ETV</h3>
                <input
                    type="file"
                    accept="video/*"
                    id="videoInput"
                    style={{ display: "none" }}
                    onChange={handleVideoUpload}
                />
                <div>
                    <button
                        className="button"
                        style={{ backgroundColor: "red" }}
                        onClick={handleVideoDelete}
                    >
                        Padam
                    </button>

                    <button className='button'
                        onClick={() => document.getElementById("videoInput").click()}
                        disabled={isUploading}>
                        {isUploading ? 'Uploading...' : (videoUrl ? 'Tukar' : 'Tambah')}
                    </button>
                </div>

            </div>

            {videoUrl ? (
                <div
                    style={{
                        marginBottom: '0px',
                        border: '2px dashed #ccc',  // Outline with a dashed style
                        padding: '0px',            // Add padding around the video
                        borderRadius: '8px',        // Rounded corners for the outline
                    }}
                >
                    <video
                        src={videoUrl}
                        muted
                        controls
                        width="100%"   // Adjust width as needed
                        style={{
                            maxHeight: '400px',        // Maximum height for the video
                            objectFit: 'cover',        // Maintain aspect ratio and cover the space
                            borderRadius: '8px',       // Rounded corners for the video itself
                            outline: 'none'            // Disable default outline if any
                        }}
                    />


                </div>
            ) : (
                <div
                    style={{
                        marginBottom: '0px',
                        border: '2px dashed #ccc',  // Same border style for empty state
                        padding: '0px',            // More padding to make space for the message
                        textAlign: 'center',        // Center the text message
                        borderRadius: '8px',        // Rounded corners
                        color: '#777',
                        height: '100px'
                    }}
                >
                    Sila masukkan video
                </div>
            )}


            <hr style={{ margin: "30px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                <h3>Setting ETV</h3>
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                    {isAutoSaving ? "Menyimpan..." : autoSavedAt ? `Auto-saved ✓` : ""}
                </div>
            </div>

            <hr style={{ margin: "30px 0" }} />

            <div style={{ marginBottom: "20px", padding: "0 30px" }}>
                <h3>Force Gelap (Blackout)</h3>

                <button
                    className="button"
                    onClick={() => setForceBlackout(v => !v)}
                    style={{
                        background: forceBlackout ? "#b00020" : "#666",
                    }}
                    title="Bila ON, TV akan jadi gelap"
                >
                    {forceBlackout ? "Blackout: ON" : "Blackout: OFF"}
                </button>

                <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>
                    ON = papar skrin hitam (force gelap). OFF = papar seperti biasa.
                </div>
            </div>

            <div className="setting-panel">
                {['Subuh', 'Zohor', 'Jumaat', 'Asar', 'Maghrib', 'Isyak'].map((prayerSetting) => (
                    <div key={prayerSetting} className="prayer-time">
                        <h3>{prayerSetting}</h3>
                        <div className="input-group">
                            <label style={{ fontSize: '14px' }}>Masa Iqamah:</label>
                            <input
                                type="number"
                                value={values[prayerSetting].masaIqamah}
                                onChange={(e) => handleInputChange(e, prayerSetting, 'masaIqamah')}
                                style={{ padding: '10px', fontSize: '18px', fontWeight: 'bold' }}
                            />
                        </div>
                        <div className="input-group">
                            <label style={{ fontSize: '14px' }}>Skrin Gelap:</label>
                            <input
                                type="number"
                                value={values[prayerSetting].skrinGelap}
                                onChange={(e) => handleInputChange(e, prayerSetting, 'skrinGelap')}
                                style={{ padding: '10px', fontSize: '18px', fontWeight: 'bold' }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {showMeccaButton && (
                <div style={{ marginBottom: "20px", padding: "0 30px 30px" }}>
                    <h3>Mecca Live</h3>

                    <button
                        className="button"
                        onClick={() => setMeccaLive((v) => !v)}
                        style={{
                            background: meccaLive ? "#1f7a1f" : "#666",
                        }}
                        title="Toggle Mecca Live"
                    >
                        {meccaLive ? "Mecca Live: ON" : "Mecca Live: OFF"}
                    </button>
                </div>
            )}




            <div style={{ marginBottom: "20px", padding: "0 30px 0px" }}>
                <h3>Pilihan Warna ETV</h3>

                <div className="color-grid">
                    {[
                        "#7A1777", "#990000", "#660000", "#783f04", "#7f6000",
                        "#134f5c", "#0c343d", "#073763", "#351c75", "#20124d", "#444444"
                    ].map((color) => {
                        const selected = color === etvColor;
                        return (
                            <button
                                key={color}
                                type="button"
                                className={`color-swatch ${selected ? "selected" : ""}`}
                                onClick={() => setEtvColor(color)}
                                title={color}
                                aria-label={`Pilih warna ${color}`}
                            >
                                <span className="color-box" style={{ backgroundColor: color }} />
                                {selected && <span className="color-check">✓</span>}
                            </button>
                        );
                    })}
                </div>

                <div className="color-hint">
                    Dipilih: <span className="color-code">{etvColor}</span>
                </div>
            </div>
            <hr style={{ margin: "30px 0" }} />

            <div style={{ marginBottom: "20px", padding: "0 30px 0px" }}>
                <h3>Bunyi ETV</h3>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {[
                        { key: "beep", label: "Beep" },
                        { key: "beduk", label: "Beduk" },
                    ].map((opt) => {
                        const active = etvSound === opt.key;
                        return (
                            <button
                                key={opt.key}
                                type="button"
                                className="button"
                                onClick={() => setEtvSound(opt.key)}
                                style={{ background: active ? "#1f7a1f" : "#666" }}
                            >
                                {active ? `✓ ${opt.label}` : opt.label}
                            </button>
                        );
                    })}
                </div>

                <div style={{ fontSize: 13, opacity: 0.8, marginTop: 8 }}>
                    Dipilih: <b>{etvSound}</b>
                </div>
            </div>




            <hr style={{ margin: "30px 0" }} />

            <ZonJakim />
            <div style={{ height: "50px" }}></div>
        </div>
    );
};

export default Etv;
